import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const aboutHeroBg = 'https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20illustration%20of%20a%20cozy%20reading%20corner%20with%20bookshelves%20filled%20with%20colorful%20manga%20volumes%2C%20soft%20natural%20light%20streaming%20through%20windows%2C%20comfortable%20armchair%2C%20warm%20golden%20and%20wood%20tones%2C%20minimalist%20and%20artistic%20composition%2C%20peaceful%20atmosphere&width=1600&height=500&seq=manga-about-hero&orientation=landscape';

const teamBg = 'https://readdy.ai/api/search-image?query=A%20creative%20team%20working%20together%20in%20a%20modern%20cozy%20office%20with%20manga%20posters%20on%20walls%2C%20warm%20lighting%2C%20collaborative%20atmosphere%2C%20diverse%20young%20professionals%2C%20casual%20and%20creative%20vibe%2C%20soft%20focus%2C%20editorial%20photography%20style&width=800&height=600&seq=manga-team-img&orientation=portrait';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9ooqe8senbniolesj40', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed as { meta?: { message?: string; detail?: string }; message?: string })?.meta?.message
          || (parsed as { meta?: { message?: string; detail?: string }; message?: string })?.meta?.detail
          || (parsed as { message?: string })?.message
          || responseText;
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '土豪漫画网',
    url: 'https://tuhaomanhua.com',
    description: '土豪漫画网是专注于韩漫推荐、日漫大全、国漫排行榜的中文漫画分类网站。',
    email: 'contact@tuhaomanhua.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '北京市',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@tuhaomanhua.com',
      availableLanguage: ['Chinese'],
    },
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 土豪漫画网',
    url: 'https://tuhaomanhua.com/about',
    description: '了解土豪漫画网的品牌故事、核心团队与联系方式，专注韩漫推荐、日漫大全、国漫排行榜。',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '土豪漫画网是如何创立的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '土豪漫画网由一群热爱漫画的年轻人于2020年创立。创始团队发现市场上缺乏一个按韩漫推荐、日漫大全、国漫排行榜精准分类的漫画导航平台，于是决定亲手打造一个能让所有漫画爱好者快速找到心仪漫画的网站。',
        },
      },
      {
        '@type': 'Question',
        name: '土豪漫画网的内容更新频率是怎样的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '土豪漫画网的内容团队每日追踪最新漫画更新动态，确保韩漫推荐、日漫大全、国漫排行榜三大频道的漫画信息始终保持最新状态，让漫画爱好者第一时间获取更新资讯。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系土豪漫画网的客服？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过以下方式联系我们：发送邮件至 contact@tuhaomanhua.com，或通过网站上提供的联系表单提交您的意见与建议。我们的客服团队会在24小时内回复您的咨询。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <ScrollToTop />

        <main className="pt-16 md:pt-18">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />

            {/* Hero */}
            <section className="relative w-full h-[300px] md:h-[400px] rounded-2xl overflow-hidden mx-4 md:mx-6 mt-4">
              <div className="absolute inset-0">
                <img
                  src={aboutHeroBg}
                  alt="关于土豪漫画网"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-3">
                  关于我们
                </h1>
                <p className="text-background-50/80 text-sm md:text-base max-w-xl">
                  专注韩漫推荐、日漫大全、国漫排行榜，为漫画爱好者而生
                </p>
              </div>
            </section>

            {/* Brand Introduction */}
            <section className="w-full px-4 md:px-6 py-14 md:py-20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
                <div>
                  <span className="text-xs font-semibold text-primary-500 uppercase tracking-wider mb-2 block">
                    品牌故事
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                    从漫画爱好者到漫画平台
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    <p>
                      土豪漫画网诞生于2020年，由一群热爱漫画的年轻人共同创立。创始人团队本身都是资深的漫画爱好者，在多年的漫画阅读经历中，发现了一个共同的痛点——市面上的漫画网站种类繁多但分类混乱，读者很难快速找到自己想看的漫画类型。
                    </p>
                    <p>
                      于是，我们决定亲手打造一个按<strong>韩漫推荐</strong>、<strong>日漫大全</strong>、<strong>国漫排行榜</strong>精准分类的漫画导航平台。"土豪"这个名字，代表我们希望每位访问者都能像"土豪"一样，在这个平台上随心所欲地找到任何想看的漫画——不是因为价格，而是因为丰富和便捷。
                    </p>
                    <p>
                      经过多年的精心运营，土豪漫画网已经收录了数百部优质漫画作品，每日为数万名漫画爱好者提供漫画推荐与分类导航服务。
                    </p>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={teamBg}
                    alt="土豪漫画网团队"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </section>

            {/* Reputation / Stats */}
            <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                    用户口碑
                  </h2>
                  <p className="text-foreground-600 text-sm md:text-base">
                    多年来，我们始终以用户需求为核心，赢得了广大漫画爱好者的信赖与支持
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6 mb-14">
                  {[
                    { value: '50万+', label: '月活跃用户' },
                    { value: '300+', label: '收录漫画' },
                    { value: '98%', label: '用户好评率' },
                    { value: '4年+', label: '持续运营' },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center p-5 bg-background-50 rounded-xl">
                      <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">
                        {stat.value}
                      </div>
                      <div className="text-xs md:text-sm text-foreground-600">{stat.label}</div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {[
                    {
                      name: '漫画迷小王',
                      text: '作为一个追了十年漫画的老读者，土豪漫画网的分类真的很方便。韩漫推荐、日漫大全、国漫排行榜分得清清楚楚，找漫画再也不用来回翻了。',
                    },
                    {
                      name: '日漫爱好者小陈',
                      text: '日漫大全频道太棒了！从海贼王到最新番剧，日漫资源非常全面，更新也很快。已经推荐给身边的漫画朋友们了。',
                    },
                    {
                      name: '国漫忠实粉丝阿杰',
                      text: '国漫排行榜是我每天必刷的频道，一人之下、斗罗大陆的更新信息总能第一时间看到。支持国漫，支持土豪漫画网！',
                    },
                    {
                      name: '韩漫爱好者小美',
                      text: '女神降临、看脸时代、我独自升级……好看的韩漫推荐这里全都有，真的是韩漫迷的宝藏网站！',
                    },
                  ].map((review) => (
                    <div key={review.name} className="bg-background-50 rounded-xl p-5 md:p-6">
                      <div className="flex items-center gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-4">{review.text}</p>
                      <p className="text-xs font-medium text-foreground-500">—— {review.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Contact Section */}
            <section className="w-full px-4 md:px-6 py-14 md:py-20">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                    联系我们
                  </h2>
                  <p className="text-foreground-600 text-sm md:text-base">
                    有任何建议或合作意向，欢迎随时与我们取得联系
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
                  <div className="bg-background-100 rounded-xl p-6 text-center">
                    <span className="w-12 h-12 flex items-center justify-center bg-primary-100 text-primary-500 rounded-xl mx-auto mb-4">
                      <i className="ri-mail-line text-xl"></i>
                    </span>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-2">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">contact@tuhaomanhua.com</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-6 text-center">
                    <span className="w-12 h-12 flex items-center justify-center bg-accent-100 text-accent-500 rounded-xl mx-auto mb-4">
                      <i className="ri-map-pin-line text-xl"></i>
                    </span>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-2">公司地址</h3>
                    <p className="text-sm text-foreground-600">北京市朝阳区漫画创意园A座</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-6 text-center">
                    <span className="w-12 h-12 flex items-center justify-center bg-secondary-100 text-secondary-500 rounded-xl mx-auto mb-4">
                      <i className="ri-time-line text-xl"></i>
                    </span>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-2">工作时间</h3>
                    <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6 text-center">
                    发送消息
                  </h3>
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-4 max-w-lg mx-auto"
                  >
                    <input type="text" name="website_alt" className="honeypot-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-500 mt-1 text-right">最多500字</p>
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>

                    {formStatus === 'success' && (
                      <div className="text-center py-3 px-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-700 font-medium">消息已发送成功！我们会尽快回复您。</p>
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="text-center py-3 px-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">{formError}</p>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}